import { Component, OnInit, AfterViewInit, ElementRef, LOCALE_ID } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ErrorHandler } from './../../../globals/classes/errorHandler';
import { UserInterface, Header } from './../../classes/userInterface';
import { AppSettings } from '../../app.settings';
import { Extensions } from '../../classes/static/extensions';
import { ErrorMsgModalComponent } from '../error-msg-modal/error-msg-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ChangePasswordComponent } from '../change-password/change-password.component';
import { PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as moment from 'moment';
import { LanguageService } from '@ibank-globals/language';
import { take } from 'rxjs/operators';
import { AutoRefreshTimerService } from '../../services/auto-refresh-timer.service';
import { BaseComponent } from '../../../globals/classes/base.component';
import { ErrorDescription, SourceApis } from '../../../globals/classes/errorDescription';
import { ErrorMessageModalComponent } from '../error-message-modal/error-message-modal.component';
import { ErrorMessage, ErrorMessages } from '../../classes/error-messages-new';
import { CookieService } from '@nbg-globals/cookie';
declare const ga: any;

@Component({
	selector: 'index-page',
	template: require('./index.component.html'),
})
export class IndexComponent extends BaseComponent implements OnInit, AfterViewInit {
	public autoRefreshService = AutoRefreshTimerService;
	public isNewErrorModal: boolean = AppSettings.isNewErrorModalEnabled;
	public newError: ErrorMessage;

	constructor(
		@Inject(PLATFORM_ID) private platformId: Object,
		private router: Router,
		private elRef: ElementRef,
		private modalService: NgbModal,
		private languageSvc: LanguageService,
		@Inject(LOCALE_ID) private _locale: string
	) {
		super();
		moment.locale(this._locale);
	}

	public ngOnInit(): void {
		this.initUI();
		this.setupSubscriptions();
	}

	public ngAfterViewInit(): void {
		AppSettings.load();
		this.elRef.nativeElement.querySelector('.appRoot').focus();
		this.initGoogleAnalytics();
		AutoRefreshTimerService.start();
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();
		AutoRefreshTimerService.stop();
	}

	private setupSubscriptions(): void {
		this.mortalize(ErrorHandler.errorObs).subscribe((error) => this.handleAppError(error));
	}

	private initUI() {
		UserInterface.header = Header.Init();
	}

	private initGoogleAnalytics() {
		this.router.events.subscribe((event) => {
			if (event instanceof NavigationEnd && isPlatformBrowser(this.platformId)) {
				CookieService.runGoogleAnalyticsCommand('set', 'page', event.urlAfterRedirects);
				CookieService.runGoogleAnalyticsCommand('send', 'pageview');
			}
		});
	}

	private handleAppError(error: ErrorDescription) {
		// When the error carries explicit modal display overrides,
		// render the new redesigned modal regardless of feature flags.
		if (error && error.hasModalOverrides) {
			this._openRedesignedErrorModal(error);
			return;
		}

		if (!error || !error.desc || error.code == 'SYSTEM') {
			if (this.isNewErrorModal) {
				const modalRef = this.modalService.open(ErrorMsgModalComponent, {
					windowClass: 'modal-xs',
				});
				(modalRef.componentInstance as ErrorMsgModalComponent).icon = 'error-4';
				(modalRef.componentInstance as ErrorMsgModalComponent).title = 'Κάτι δεν πήγε καλά';
				(modalRef.componentInstance as ErrorMsgModalComponent).action = 'Ενημερώθηκα';
				(modalRef.componentInstance as ErrorMsgModalComponent).message =
					'Συγνώμη αλλά κάτι δεν πήγε καλά και η διαδικασία διακόπηκε. Παρακαλούμε προσπαθήστε ξανά, και αν το πρόβλημα παραμένει επικοινωνήστε μαζί μας στο τηλέφωνο 210 4848484.';
				return;
			} else {
				this.modalService.open(ErrorMsgModalComponent);
				return;
			}
		}

		if (error.sourceApi == SourceApis.Identity) {
			this.handleIdentityError(error);
		} else {
			this.handleOnboardingError(error);
		}
	}

	private handleIdentityError(error: ErrorDescription) {
		var errorCode = parseInt(error.code);
		if (Extensions.isLinkUserErrorCode(errorCode)) return;

		if (error.desc === 'password-expired') {
			if (AppSettings.isNewChangePasswordModals) {
				this.modalService.open(ChangePasswordComponent, {
					windowClass: 'modal-full',
				});
			} else this.modalService.open(ChangePasswordComponent);
		} else {
			if (this.isNewErrorModal) {
				this.newError = this.extractNewErrorMessage(errorCode, error.desc);
			}
			this.languageSvc
				.translate(
					'login',
					this.isNewErrorModal
						? this.newError.message
						: this.extractErrorMessage(errorCode, error.desc)
				)
				.pipe(take(1))
				.subscribe((tranlation) => {
					let modalRef;
					if (this.isNewErrorModal) {
						modalRef = this._openIdentityErrorMsgModal(modalRef, errorCode, error);
					} else {
						if (error.desc === 'credentials-error') {
							modalRef = this.modalService.open(ErrorMessageModalComponent, {
								windowClass: 'modal-xs',
							});
							(modalRef.componentInstance as ErrorMessageModalComponent).title =
								'Σφάλμα στο Username ή στο Password';
							(modalRef.componentInstance as ErrorMessageModalComponent).action =
								'Εντάξει';
							(modalRef.componentInstance as ErrorMessageModalComponent).body =
								this.extractErrorMessage(errorCode, error.desc);
						} else {
							modalRef = this.modalService.open(ErrorMsgModalComponent);
						}
					}
					if (!!tranlation) {
						modalRef.componentInstance.errorMessage = tranlation;
					}
				});
		}
	}

	private _openIdentityErrorMsgModal(modalRef: any, errorCode: number, error: ErrorDescription) {
		modalRef = this.modalService.open(ErrorMsgModalComponent, {
			windowClass: 'modal-xs',
		});
		(modalRef.componentInstance as ErrorMsgModalComponent).icon = this.newError.icon;
		(modalRef.componentInstance as ErrorMsgModalComponent).title = this.newError.title;
		(modalRef.componentInstance as ErrorMsgModalComponent).action = this.newError.action;
		(modalRef.componentInstance as ErrorMsgModalComponent).message = this.newError.message;
		return modalRef;
	}

	private _openRedesignedErrorModal(error: ErrorDescription) {
		const modalRef = this.modalService.open(ErrorMsgModalComponent, {
			windowClass: 'modal-xs',
		});
		const instance = modalRef.componentInstance as ErrorMsgModalComponent;
		instance.icon = error.modalIcon || 'error-x';
		instance.title = error.modalTitle || 'Κάτι δεν πήγε καλά';
		instance.message = error.modalMessage || this.extractErrorMessage(parseInt(error.code), error.desc);
		instance.action = error.modalAction || 'Ενημερώθηκα';
	}

	private handleOnboardingError(error: ErrorDescription) {
		var errorCode = parseInt(error.code);
		if (this.isNewErrorModal) {
			this.newError = this.extractNewOnboardingErrorMessage(errorCode);
		}
		this.languageSvc
			.translate(
				'login',
				this.isNewErrorModal
					? this.newError.message
					: this.extractOnboardingErrorMessage(errorCode)
			)
			.pipe(take(1))
			.subscribe((tranlation) => {
				if (this.isNewErrorModal) {
					const modalRef = this.modalService.open(ErrorMsgModalComponent, {
						windowClass: 'modal-xs',
					});
					(modalRef.componentInstance as ErrorMsgModalComponent).icon =
						this.newError.icon;
					(modalRef.componentInstance as ErrorMsgModalComponent).title =
						this.newError.title;
					(modalRef.componentInstance as ErrorMsgModalComponent).action =
						this.newError.action;
					(modalRef.componentInstance as ErrorMsgModalComponent).message =
						this.newError.message;
				} else {
					const modalRef = this.modalService.open(ErrorMsgModalComponent);

					if (!!tranlation) {
						modalRef.componentInstance.errorMessage = tranlation;
					}
					var specialIcon = this.extractOnboardingErrorIcon(errorCode);
					if (!!specialIcon) {
						modalRef.componentInstance.icon = specialIcon;
					}
				}
			});
	}

	private extractOnboardingErrorMessage(code: number): string {
		if (Extensions.isOnboardMissingValuesErrorCode(code))
			return this.getOnBoardMissingValuesMessage(code);
		if (Extensions.isOnBoardUnexpectedErrorCode(code))
			return this.getOnBoardUnexpectedErrorMessage(code);
		if (Extensions.isOnBoardVerificationErrorCode(code))
			return this.getOnBoardVerificationErrorMessage(code);
		if (Extensions.isOnBoardPersonalDetailsErrorCode(code))
			return this.getOnBoardPersonalDetailsErrorMessage(code);
		if (Extensions.isOnBoardEmailErrorCode(code)) return this.getOnBoardEmailErrorMessage(code);
		if (Extensions.isOnBoardInternetBankingErrorCode(code))
			return this.getOnBoardInternetBankingErrorMessage(code);
		if (Extensions.isOnBoardValidityErrorCode(code))
			return this.getOnBoardValidityErrorMessage(code);
		else return 'Onboarding API error message';
	}
	private extractNewOnboardingErrorMessage(code: number): ErrorMessage {
		if (Extensions.isOnBoardNewErrorCodes(code))
			return ErrorMessages.getOnboardingErrorMessageByCode(code);
		if (
			Extensions.isOnBoardUnexpectedErrorCode(code) ||
			Extensions.isOnBoardVerificationErrorCode(code)
		)
			return ErrorMessages.getGeneralErrorMessage('onboardInternetBanking');
		else return ErrorMessages.getGeneralErrorMessage('general');
	}

	private extractOnboardingErrorIcon(code: number): string {
		switch (code) {
			case 1001:
				return 'wrong-card-pin';
			case 1012:
				return 'bank-visit';
			case 7010:
				return 'wrong-email';
			default:
				return null;
		}
	}

	private extractNewErrorMessage(code: number, description: string): ErrorMessage {
		if (Extensions.isIdentityNewErrorCodes(code))
			return ErrorMessages.getIdentityErrorMessageByCode(code);
		if (Extensions.isOtpErrorCode(code))
			return ErrorMessages.getGeneralErrorMessage('general2FAErrors');
		if (Extensions.isTwofaErrorCode(code))
			return ErrorMessages.getGeneralErrorMessage('generalOtpErrors');
		if (description) return ErrorMessages.getIdentityErrorMessageByDesc(description);
		else return ErrorMessages.getGeneralErrorMessage('general');
	}

	private extractErrorMessage(code: number, description: string): string {
		if (Extensions.isOtpErrorCode(code)) return this.getOtpErrorMessage(code);
		if (Extensions.isTwofaErrorCode(code)) return this.getTwofaErrorMessage(code);
		if (code == 1999) return 'Παρουσιάστηκε κάποιο σφάλμα. Παρακαλούμε προσπαθήστε ξανά';
		if (description == 'OldPasswordWentWrong')
			return 'Εισάγατε λάθος Password. Παρακαλούμε προσπαθήστε ξανά.';
		if (description == 'invalid-userLevel')
			return 'Δεν μπορείτε να συνδεθείτε.Παρακαλώ επικοινωνήστε με το νόμιμο εκπρόσωπο της εταιρίας σας.';
		else return this.getErrorFromExceptionDescription(description);
	}
	private getOnBoardPersonalDetailsErrorMessage(code: number): string {
		switch (code) {
			case 1012:
				return 'Η έκδοση κωδικών δε μπορεί να ολοκληρωθεί διότι κάποια στοιχεία σας στην Τράπεζα είναι ελλιπή. Παρακαλούμε απευθυνθείτε σε ένα Κατάστημα';
			case 1015:
				return 'Η έκδοση κωδικών δε μπορεί να ολοκληρωθεί διότι κάποια στοιχεία σας στην Τράπεζα είναι ελλιπή. Παρακαλούμε απευθυνθείτε σε ένα Κατάστημα';
			case 1016:
				return 'Η έκδοση κωδικών δε μπορεί να ολοκληρωθεί διότι κάποια στοιχεία σας στην Τράπεζα είναι ελλιπή. Παρακαλούμε απευθυνθείτε σε ένα Κατάστημα';
			case 1017:
				return 'Η έκδοση κωδικών δε μπορεί να ολοκληρωθεί διότι η Ταυτότητα απουσιάζει από τα στοιχεία σας. Παρακαλούμε απευθυνθείτε σε ένα Κατάστημα';
			case 1018:
				return 'Η έκδοση κωδικών δε μπορεί να ολοκληρωθεί διότι το ΑΦΜ απουσιάζει από τα στοιχεία σας. Παρακαλούμε απευθυνθείτε σε ένα Κατάστημα';
			case 1021:
				return 'Παρουσιάστηκε πρόβλημα. Παρακαλούμε δοκιμάστε ξανά  και αν το πρόβλημα παραμένει, επικοινωνήστε μαζί μας στο contact.center@nbg.gr';
			case 1025:
				return 'Η έκδοση κωδικών δε μπορεί να ολοκληρωθεί διότι το ‘Ονομα / Πατρώνυμο απουσιάζει από τα στοιχεία σας. Παρακαλούμε απευθυνθείτε σε ένα Κατάστημα';
			default:
				return 'Παρουσιάστηκε κάποιο σφάλμα. Παρακαλούμε προσπαθήστε ξανά';
		}
	}

	private getOnBoardUnexpectedErrorMessage(code: number): string {
		switch (code) {
			default:
				return 'Παρουσιάστηκε κάποιο σφάλμα. Παρακαλούμε προσπαθήστε ξανά';
		}
	}

	private getOnBoardValidityErrorMessage(code: number): string {
		switch (code) {
			case 5004:
				return 'Παρουσιάστηκε κάποιο σφάλμα. Παρακαλούμε δοκιμάστε να καταχωρίσετε ξανά μία έγκυρη διεύθυνση ηλεκτρονικού ταχυδρομίου, στο αντίστοιχο βήμα';
			case 5019:
				return 'Παρουσιάστηκε κάποιο σφάλμα. Παρακαλούμε απευθυνθείτε σε ένα Κατάστημα';
			case 5200:
				return 'Δεν έχετε συμπληρώσει το 18ο έτος της ηλικίας σας για να δημιουργήσετε ibank κωδικούς.';
			default:
				return 'Παρουσιάστηκε κάποιο σφάλμα. Παρακαλούμε προσπαθήστε ξανά';
		}
	}
	private getOnBoardMissingValuesMessage(code: number): string {
		switch (code) {
			case 4009:
				return 'Παρουσιάστηκε κάποιο σφάλμα. Παρακαλούμε προσπαθήστε ξανά αύριο, χρησιμοποιώντας ένα διαφορετικό email';
			case 4019:
				return 'Παρουσιάστηκε πρόβλημα κατά την εγγραφή. Παρακαλούμε δοκιμάστε ξανά και αν το πρόβλημα παραμένει, επικοινωνήστε μαζί μας στο contact.center@nbg.gr';
			default:
				return 'Παρουσιάστηκε κάποιο σφάλμα. Παρακαλούμε προσπαθήστε ξανά';
		}
	}

	private getOnBoardInternetBankingErrorMessage(code: number): string {
		switch (code) {
			default:
				return 'Παρουσιάστηκε πρόβλημα κατά την εγγραφή. Παρακαλούμε δοκιμάστε ξανά και αν το πρόβλημα παραμένει, επικοινωνήστε μαζί μας στο contact.center@nbg.gr';
		}
	}
	private getOnBoardVerificationErrorMessage(code: number): string {
		switch (code) {
			case 60011:
				return 'Ο κωδικός που καταχωρίσατε δεν είναι σωστός';
			case 60012:
				return 'Έχετε καταχωρίσει περισσότερες από 3 φορές τον κωδικό, προσπαθήστε ξανά μετά από 60 λεπτά';
			case 60013:
				return 'Ο κωδικός που καταχωρίσατε έχει λήξει. Προσπαθήστε πάλι με έναν νέο κωδικό';
			case 60014:
				return 'Έχετε καταχωρίσει περισσότερες από 3 φορές τον κωδικό, προσπαθήστε ξανά μετά από 60 λεπτά';
			default:
				return 'Παρουσιάστηκε κάποιο σφάλμα. Παρακαλούμε προσπαθήστε ξανά';
		}
	}

	private getOnBoardEmailErrorMessage(code: number): string {
		switch (code) {
			case 7004:
				return 'Παρουσιάστηκε κάποιο σφάλμα. Παρακαλούμε προσπαθήστε ξανά αύριο, χρησιμοποιώντας ένα διαφορετικό email.';
			case 7010:
				return 'Αυτό το e-mail δεν μπορεί να χρησιμοποιηθεί. Παρακαλούμε δοκιμάστε ένα άλλο.';
			default:
				return 'Παρουσιάστηκε κάποιο σφάλμα. Παρακαλούμε προσπαθήστε ξανά';
		}
	}

	private getOtpErrorMessage(code: number): string {
		switch (code) {
			case 7002:
				return 'Λανθασμένος Ηλεκτρονικός Κλειδάριθμος';
			case 7003:
				return 'Έχετε φτάσει στο μέγιστο αριθμό λανθασμένων προσπαθειών εισαγωγής OTP. Παρακαλούμε επικοινωνήστε με το HelpDesk';
			case 7004:
				return 'Το OTP που δώσατε έχει ξαναχρησιμοποιηθεί. Παρακαλούμε δοκιμάστε ξανά με διαφορετικό OTP';
			case 7005:
				return 'Παρουσιάστηκε κάποιο σφάλμα. Παρακαλούμε βεβαιωθείτε πως η συσκευή Ηλεκτρονικού Κλειδάριθμου είναι ενεργοποιημένη';
			default:
				return 'Παρουσιάστηκε κάποιο σφάλμα κατά τον έλεγχο του OTP, παρακαλούμε επικοινωνήστε με το HelpDesk';
		}
	}

	private getTwofaErrorMessage(code: number): string {
		switch (code) {
			case 6001:
				return 'Η διαδικασία σύνδεσης με OTP απέτυχε. Παρακαλούμε βεβαιωθείτε πως ο φυλλομετρητής (browser) σας επιτρέπει την αποθήκευση cookies και δοκιμάστε ξανά';
			case 6002:
				return 'Λανθασμένος Ηλεκτρονικός Κλειδάριθμος';
			default:
				return 'Παρουσιάστηκε κάποιο σφάλμα κατά τη διαδικασία σύνδεσης με OTP, παρακαλούμε δοκιμάστε ξανά αργότερα';
		}
	}

	private getErrorFromExceptionDescription(description: string): string {
		switch (description) {
			case 'credentials-error':
				return 'Το Username ή/και το Password που πληκτρολογήσατε δεν είναι σωστό. Παρακαλούμε προσπαθήστε ξανά';
			case 'system-error':
			case 'Authentication process is not initialized.':
				return 'Παρουσιάστηκε σφάλμα, δοκιμάστε αργότερα.';
			case 'Service Unavailable':
				return 'Η υπηρεσία δεν είναι προσωρινά διαθέσιμη.';
			case 'Invalid OTP.':
				return 'Λανθασμένος Ηλεκτρονικός Κλειδάριθμος.';
			case 'Invalid code':
			case 'Incorrect Verification Code':
				return 'Λανθασμένος κωδικός επιβεβαίωσης. Παρακαλούμε βεβαιωθείτε πως έχετε πληκτρολογήσει σωστά και δοκιμάστε ξανά.';
			case 'Max number of verification retries exceeded.':
				return 'Έχετε φτάσει στο μέγιστο αριθμό λανθασμένων προσπαθειών εισαγωγής κωδικού επιβεβαίωσης. Παρακαλούμε δοκιμάστε ξανά αργότερα.';
			case 'Verification code has expired':
				return "Ο κωδικός επιβεβαίωσης έχει λήξει. Μπορείτε να επιλέξετε 'επανάληψη αποστολής' για να σταλεί νέος";
			case 'Invalid UserId.':
				return 'Μη αποδεκτό Username.';
			case 'OTP maximum retries reached.':
				return 'Έχετε φτάσει στο μέγιστο αριθμό λανθασμένων προσπαθειών εισαγωγής OTP. Παρακαλούμε επικοινωνήστε με το HelpDesk.';
			case 'Unrecognized OTP error.':
				return 'Παρουσιάστηκε σφάλμα κατά τη διαδικασία αποστολής OTP. Παρακαλούμε επικοινωνήστε με το HelpDesk.';
			case 'Problematic OTP device.':
				return 'Παρουσιάστηκε σφάλμα σχετικά με τη συσκευή Ηλεκτρονικού Κλειδάριθμου. Παρακαλούμε επικοινωνήστε με το HelpDesk.';
			case 'OTP is already used.':
				return 'Το OTP που δώσατε έχει ξαναχρησιμοποιηθεί. Παρακαλούμε δοκιμάστε ξανά με διαφορετικό OTP.';
			case 'Invalid Credentilas':
				return 'Έχετε εισάγει λάθος αριθμό κάρτας ή/και PIN. Παρακαλώ προσπαθήστε ξανά.';
			case "User's account is self-locked":
				return 'Ο λογαριασμός σας έχει απενεργοποιηθεί για λόγους ασφαλείας, μετά από πιθανή απάτη ή/και κλοπή στοιχείων. Εάν θελήσετε να τον ανακτήσετε, καλέστε μας στο +30 210 77 27 440.';
			default:
				return description;
		}
	}
}
